import type { SortDirection } from '@data/components/basic/Table/types';
import { useCallback, useState } from 'react';

export interface UseTableControlsProps {
  defaultSortColumn: string;
  defaultSortDirection?: SortDirection;
  defaultPageSize?: number;
}

export function useTableControls({
  defaultSortColumn,
  defaultSortDirection = 'asc',
  defaultPageSize = 10,
}: UseTableControlsProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortColumn, setSortColumn] = useState<string | undefined>(
    defaultSortColumn
  );
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(
    defaultSortDirection
  );

  const [userSorted, setUserSorted] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerms, setSearchTerms] = useState('');

  const reset = useCallback(() => {
    setSortColumn(defaultSortColumn);
    setSortDirection(defaultSortDirection);
    setUserSorted(false);
    setPage(1);
    setSearchInput('');
    setSearchTerms('');
  }, [defaultSortColumn, defaultSortDirection]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      // Clearing the search restores the default sort.
      if (value.trim() === '' && searchTerms !== '') {
        setSearchTerms('');
        setSortColumn(defaultSortColumn);
        setSortDirection(defaultSortDirection);
        setUserSorted(false);
        setPage(1);
      }
    },
    [searchTerms, defaultSortColumn, defaultSortDirection]
  );

  const handleSearchSubmit = useCallback((value: string) => {
    // Submitting a search clears the sort so backend relevance order is used.
    setSearchTerms(value);
    setSortColumn(undefined);
    setSortDirection(undefined);
    setUserSorted(false);
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (col: string | undefined, dir: SortDirection | undefined) => {
      if (col === undefined || dir === undefined) {
        // Clearing sort always unsets — no orderBy sent, backend default order.
        // (reset() is the way to restore the configured default sort.)
        setSortColumn(undefined);
        setSortDirection(undefined);
        setUserSorted(false);
      } else {
        setSortColumn(col);
        setSortDirection(dir);
        setUserSorted(true);
      }
      setPage(1);
    },
    []
  );

  return {
    // Pre-shaped to match Table's prop names so pages can spread directly:
    // <Table {...tableProps} ... />
    tableProps: {
      page,
      pageSize,
      sortColumn,
      sortDirection,
      onPageChange: setPage,
      onPageSizeChange: setPageSize,
      searchValue: searchInput,
      onSearchChange: handleSearchChange,
      onSearchSubmit: handleSearchSubmit,
      onSortChange: handleSortChange,
    },
    // Not Table props — used by pages for the API call and reset logic.
    searchTerms,
    userSorted,
    reset,
  };
}
